import React, {useEffect, useState} from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import BloodPressureIcon from '../common/BloodPressureIcon'
import {useReduxAuth} from '../../../auth'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {CardCategory} from '../../../../shared/components'
import {CategorizedPatients} from '../../../../entities/entities'
import ScrollWrapper from '../ScrollWrapper'
import PatientCard from '../common/PatientCard'
import {getRandomMotivationalMessage} from '../../../../constants/motivationalMessages'
import ConfirmationModal from '../common/modals/ConfirmationModal'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'

export const statusColors: Record<AlertStatus, string> = {
  'Crisis Alerts': 'crisis',
  'Stage 2 Alert': 'stage2',
  'Stage 1 Alert': 'stage1',
  Elevated: 'elevated',
  Low: 'low',
}
export type AlertStatus = 'Crisis Alerts' | 'Stage 2 Alert' | 'Stage 1 Alert' | 'Elevated' | 'Low'

const BloodPressureAlerts: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const [patients, setPatients] = useState<CategorizedPatients>({
    hypertensionCrisis: [],
    hypertensionStage2: [],
    hypertensionStage1: [],
    elevated: [],
    low: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [motivationalMessage, setMotivationalMessage] = useState('')
  const [latestOnly, setLatestOnly] = useState(
    () => localStorage.getItem('bp_latest_alert_only') !== 'false'
  )
// state for modal visibility
  const [showResolveAllModal, setShowResolveAllModal] = useState(false);


  // Listen for localStorage changes to update latestOnly setting
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'bp_latest_alert_only') {
        setLatestOnly(e.newValue !== 'false')
      }
    }

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange)

    // Also check for changes in the current tab by polling localStorage
    const checkLocalStorage = () => {
      const currentValue = localStorage.getItem('bp_latest_alert_only') !== 'false'
      setLatestOnly(currentValue)
    }

    const intervalId = setInterval(checkLocalStorage, 1000) // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    // Skip if MA and no doctor selected
    if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
      setPatients({
        hypertensionCrisis: [],
        hypertensionStage2: [],
        hypertensionStage1: [],
        elevated: [],
        low: [],
      })
      setIsLoading(false)
      return
    }

    // For MA, use selected doctor ID; for others, use currentUser.id
    const doctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
      ? maSelectedDoctorId
      : currentUser?.id

    if (!doctorId) return

    setIsLoading(true)
    
    // Build query params
    const queryParams = new URLSearchParams()
    queryParams.append('latestOnly', latestOnly.toString())
    if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
      queryParams.append('doctor_id', maSelectedDoctorId.toString())
    }

    axios
      .get(API_ENDPOINTS.GET_BP_CATEGORIZED_PATIENTS(doctorId, queryParams.toString()))
      .then((res) => {
        setPatients(res.data.data.categorizedPatients)
        // Set motivational message only after API completes and if no alerts
        const categorizedPatients = res.data.data.categorizedPatients as CategorizedPatients
        if (Object.values(categorizedPatients).every((list) => list.length === 0)) {
          setMotivationalMessage(getRandomMotivationalMessage('all-patients'))
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [currentUser, latestOnly, maSelectedDoctorId]) 

  const handleResolved = (resolvedReadingId: number, categoryKey: keyof CategorizedPatients) => {
    setPatients((prev) => {
      const updated = {...prev}
      updated[categoryKey] = updated[categoryKey].filter(
        (alert) => alert.readingId !== resolvedReadingId
      )
      return updated
    })
  }

  const categoryConfigs = [
    {key: 'hypertensionCrisis', label: 'Crisis', class: 'bg-crisis'},
    {key: 'hypertensionStage2', label: 'Stage 2', class: 'bg-stage2'},
    {key: 'hypertensionStage1', label: 'Stage 1', class: 'bg-stage1'},
    {key: 'elevated', label: 'Elevated', class: 'bg-elevated'},
    // {key: 'low', label: 'Low', class: 'bg-low'}, //TODO: to enable low BP category uncomment this line
  ]

  const hasAlerts = Object.values(patients).some((list) => list.length > 0)

  const handleResolveAll = async () => {
  try { 
    await axios.post(API_ENDPOINTS.RESOLVE_ALL_READING_ALERT(),
      {},
    );
    setPatients({
      hypertensionCrisis: [],
      hypertensionStage2: [],
      hypertensionStage1: [],
      elevated: [],
      low: [],
    });
    setMotivationalMessage(getRandomMotivationalMessage('all-patients'));
  } catch (error) {
    console.error("Error resolving alerts:", error);
  }
};

  return (
    <>
    <div className={`scrollListWrapper p-4 bg-white rounded-4 border shadow-sm`}>
         <div className='d-flex align-items-center mb-3 gap-3'>
          <h3 className='m-0'>Health Alerts</h3> 
          {hasAlerts && (
            <button 
              className="btn bg-primary text-white d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
              style={{ fontSize: '0.875rem', fontWeight: '500', transition: 'all 0.2s ease' }}
              onClick={() => setShowResolveAllModal(true)}
            >
              Resolve All Alerts
            </button>        
          )}   
        </div>
      

      {isLoading ? (
        <div className='text-center py-4'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : !hasAlerts ? (
        <div className='text-center py-2'>
          <p className='text-muted mb-0 fs-5'>{motivationalMessage}</p>
        </div>
      ) : (
        <div className='scrollableList'>
          {categoryConfigs.map((cat) => {
            const list = patients[cat.key as keyof CategorizedPatients]

            // Skip entire category if no patients
            if (list.length === 0) return null

            return (
              <div key={cat.label} className='mt-3'>
                <div className='d-flex gap-2'>
                  <div className='me-2'>
                    <CardCategory
                      label={cat.label}
                      count={list.length}
                      className={cat.class}
                      icon={<BloodPressureIcon fillColor='#ffffff' width={28} height={28} />}
                    />
                  </div>
                  <div className='flex-grow-1' style={{overflow: 'hidden'}}>
                    <ScrollWrapper>
                      {list.map((entry, idx) => {
                        const p = entry.patientDetails
                        const name = `${p?.first_name} ${p?.last_name}`

                        return (
                          <PatientCard
                            key={idx}
                            name={name}
                            phone={p?.phone_number}
                            systolic={`${entry.systolic}`}
                            diastolic={`${entry.diastolic}`}
                            bpm={`${entry.pulse}`}
                            datetime={entry.lastReading}
                            patientUUID={p?.uuid}
                            readingId={entry.readingId}
                            onResolved={(id) =>
                              handleResolved(id, cat.key as keyof CategorizedPatients)
                            }
                          />
                        )
                      })}
                    </ScrollWrapper>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>

      <ConfirmationModal
        isOpen={showResolveAllModal}
        onClose={() => setShowResolveAllModal(false)}
        onConfirm={() => {
          handleResolveAll();
          setShowResolveAllModal(false);
        }}
        title="Resolve all Health Alerts"
        customContent={
          <>
            <div className="bg-light text-dark d-flex align-items-center border rounded p-3">
              <span className="me-2 text-primary">
                <i className="bi bi-info-circle-fill" style={{ fontSize: '1.3rem' }}></i>
              </span>
              <div>
                 We recommend reviewing alerts individually on each patient’s detail page to ensure nothing critical is missed.
              </div>
            </div>
            <p className="fw-semibold mt-2 ms-3">
              This will remove all current alerts. Are you sure you want to continue?
            </p>
          </>
        }
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
      />

    </>
  )
}

export default BloodPressureAlerts
